import { z } from "zod"

import { defaultIconForSubject } from "@/constants/classroomIcons"
import { CLASSROOM_SECTIONS } from "@/constants/classroomSections"

// PRD-38 §FR-26 — the create form is Ícono · Materia · Sección · Periodo.
// `grade` and `semester` are NOT collected: a program-wide period already
// encodes the year level (semester 3 IS second year), and the school year is
// derived server-side from the professor's own `semester` (§FR-27). Both
// columns survive on the model for historical rows (§FR-28) — they just have
// no input any more, so they are absent from this schema and from the create
// payload. The UPDATE path is untouched.
export const createClassroomSchema = z.object({
  // PRD-37 / ADR-0031 — subject is no longer a fixed backend enum; it's the
  // UUID of one of the professor's school-scoped Subject rows (see
  // useSubjects()). The <Select> is populated dynamically at the call site.
  subject_id: z.string().uuid(),
  // PRD-47 v2 — `education_level` left the form: the backend derives it
  // from the professor's School (admin-owned), so there is nothing to pick.
  section: z.enum(CLASSROOM_SECTIONS),
  // PRD-38 §FR-8 — the PROGRAM-WIDE period: a student is in semester 1, 3 or 9
  // of their whole program, not of one school year. The upper bound is tenant
  // config (`academic_config.periods_per_cycle`), not a static enum, so it
  // can't be a z.enum here: the select only ever offers valid options (see
  // useAcademicConfig) and the API is authoritative
  // (422 period_number_out_of_range). Defaults to 1, which is also what a
  // one-period cycle submits with the control hidden (FR-18).
  period_number: z.number().int().min(1).default(1),
  // PRD-30 — Lucide icon NAME. Always set in the form (defaults from the
  // selected subject's default_code, see CreateClassroomForm FR-10); falls
  // back to the neutral GraduationCap icon until a subject is selected.
  icon: z.string().default(defaultIconForSubject("")),
})

export type CreateClassroomInput = z.infer<typeof createClassroomSchema>

/** Preview-only: compose a label from form inputs for the creation preview line.
 * The backend derives the canonical name itself — this result is never sent in
 * the API payload; it exists so the professor sees the name they are about to
 * get. Mirrors `Classroom.canonical_label` as revised by PRD-38 §FR-30:
 * `"{subject} {period_number}°{section}"` (e.g. "Matemáticas 3°A") — the period
 * now occupies the slot `grade` used to. `subjectName` is plain text (PRD-37 —
 * subject names are school-defined free text, never translated, same treatment
 * as student names per i18n.md §11) — the caller resolves it from the selected
 * subject_id against the useSubjects() catalog. */
export function composeClassroomName(
  input: Pick<CreateClassroomInput, "period_number" | "section"> & { subjectName: string },
) {
  return `${input.subjectName} ${input.period_number}°${input.section}`
}
